import { formatters } from './formatters.js';
import { LOG_LEVELS } from './types.js';
import type {
  LogLevel,
  LoggerOptions,
  LogEntry,
  LogFormatter,
} from './types.ts';

export { formatters };
export class Logger {
  readonly #categories: string[];
  readonly #minLevel: LogLevel;
  readonly #enabled: boolean;
  readonly #formatter: LogFormatter;

  constructor(categories: string | string[] = [], options: LoggerOptions = {}) {
    this.#categories = Array.isArray(categories) ? categories : [categories];
    this.#minLevel = options.minLevel ?? 'info';
    this.#enabled = options.enabled ?? true;

    // Set up formatter
    if (typeof options.format === 'function') {
      this.#formatter = options.format;
    } else {
      this.#formatter = formatters[options.format ?? 'text'];
    }

    // Validate categories
    for (const category of this.#categories) {
      if (category.includes(':')) {
        throw new Error(
          'Categories should not contain ":" - use separate arguments or child() instead'
        );
      }
    }
  }

  /**
   * Returns all possible category patterns that could match this logger.
   * For example, for categories ['users', 'auth']:
   * ['users:auth', 'users:*', '*']
   */
  _getCategoryPatterns(): string[] {
    const patterns: string[] = [];
    const cats = this.#categories;

    // Add the full specific pattern
    patterns.push(cats.join(':'));

    // Add progressively more general patterns
    for (let i = cats.length - 1; i >= 0; i--) {
      patterns.push([...cats.slice(0, i), '*'].join(':'));
    }

    // Add the catch-all pattern
    patterns.push('*');

    return patterns;
  }

  _shouldLog(level: LogLevel): boolean {
    if (!this.#enabled) return false;
    return LOG_LEVELS[level] >= LOG_LEVELS[this.#minLevel];
  }

  _createLogEntry(
    level: LogLevel,
    message: string | unknown,
    args: unknown[]
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      category: this.#categories.join(':'),
      message,
      ...(args.length > 0 && { data: args }),
    };
  }

  _log(level: LogLevel, message: string | unknown, ...args: unknown[]): void {
    if (!this._shouldLog(level)) return;

    const entry = this._createLogEntry(level, message, args);
    const result = this.#formatter(entry);

    if (Array.isArray(result)) {
      // If formatter returns an array, spread it as multiple arguments
      console[level](...result);
    } else {
      // If formatter returns a string, use it as a single argument
      console[level](result);
    }
  }

  debug(message: string | unknown, ...args: unknown[]): void {
    this._log('debug', message, ...args);
  }

  info(message: string | unknown, ...args: unknown[]): void {
    this._log('info', message, ...args);
  }

  warn(message: string | unknown, ...args: unknown[]): void {
    this._log('warn', message, ...args);
  }

  error(message: string | unknown, ...args: unknown[]): void {
    this._log('error', message, ...args);
  }

  /**
   * Creates a child logger with additional categories
   * @param oneOrMoreCategories Additional categories to append to the current logger's categories
   * @param options Override options for this child logger
   */
  child(
    oneOrMoreCategories: string | string[],
    options: LoggerOptions = {}
  ): Logger {
    const categories = Array.isArray(oneOrMoreCategories)
      ? oneOrMoreCategories
      : [oneOrMoreCategories];

    for (const category of categories) {
      if (category.includes(':')) {
        throw new Error(
          'Child category should not contain ":" - pass an array or use multiple child() calls instead'
        );
      }
    }

    return new Logger(this.#categories.concat(categories), {
      minLevel: options.minLevel ?? this.#minLevel,
      enabled: options.enabled ?? this.#enabled,
      format: options.format ?? this.#formatter,
    });
  }

  /**
   * Get the full category path for this logger
   */
  getCategory(): string {
    return this.#categories.join(':');
  }
}
